// Sanitização de HTML de emails baseada em DOM (DOMParser), substituindo o
// antigo filtro por regex que era contornável (<svg/onload=...>, atributos sem
// aspas, tags aninhadas, etc.). O HTML nunca é executado durante o parse.

const CID_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='10'%3Eimagem%3C/text%3E%3C/svg%3E";

// Removidas por completo (tag + conteúdo)
const BLOCKED_TAGS = new Set([
  'script', 'noscript', 'iframe', 'frame', 'frameset', 'object', 'embed',
  'applet', 'link', 'meta', 'base', 'audio', 'video', 'source', 'track',
  'svg', 'math', 'template', 'dialog', 'input', 'button', 'select', 'textarea',
]);

const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'background', 'poster', 'xlink:href']);

function isDangerousUrl(value) {
  // Remove espaços e caracteres de controle usados para ofuscar (jav\tascript:)
  let v = '';
  for (const ch of String(value)) {
    if (ch.charCodeAt(0) > 32) v += ch;
  }
  v = v.toLowerCase();
  if (v.startsWith('javascript:') || v.startsWith('vbscript:')) return true;
  if (v.startsWith('data:') && !v.startsWith('data:image/')) return true;
  return false;
}

function sanitizeStyleText(css) {
  // Sem scripts no iframe o CSS não executa código, mas bloqueamos importação
  // remota e expressões legadas do IE.
  return String(css)
    .replace(/@import[^;]*(;|$)/gi, '')
    .replace(/expression\s*\(/gi, 'blocked(')
    .replace(/(javascript|vbscript)\s*:/gi, 'blocked:');
}

/**
 * Sanitiza HTML de um email para exibição.
 * @param {string} html - HTML bruto vindo do IMAP
 * @returns {string} HTML seguro (sem scripts, handlers, URLs perigosas)
 */
export function sanitizeEmailHtml(html) {
  if (!html) return '';
  let doc;
  try {
    doc = new DOMParser().parseFromString(String(html), 'text/html');
  } catch {
    return '';
  }

  const elements = Array.from(doc.querySelectorAll('*'));
  for (const el of elements) {
    if (!el.isConnected) continue; // já removido junto com um ancestral
    const tag = el.tagName.toLowerCase();

    if (BLOCKED_TAGS.has(tag)) { el.remove(); continue; }

    // Newsletters costumam envolver o corpo inteiro em <form>: remove a tag
    // preservando o conteúdo.
    if (tag === 'form') {
      while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
      el.remove();
      continue;
    }

    if (tag === 'style') {
      el.textContent = sanitizeStyleText(el.textContent || '');
      continue;
    }

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value || '';
      if (name.startsWith('on')) { el.removeAttribute(attr.name); continue; }
      if (name === 'style') {
        if (/expression\s*\(|(javascript|vbscript)\s*:|@import/i.test(value)) {
          el.setAttribute('style', sanitizeStyleText(value));
        }
        continue;
      }
      if (name === 'srcset') {
        if (/(javascript|vbscript)\s*:|data:(?!image\/)/i.test(value)) el.removeAttribute(attr.name);
        continue;
      }
      if (URL_ATTRS.has(name) && isDangerousUrl(value)) {
        el.removeAttribute(attr.name);
      }
    }

    if (tag === 'img') {
      const src = (el.getAttribute('src') || '').trim();
      if (/^cid:/i.test(src)) {
        el.setAttribute('src', CID_PLACEHOLDER);
        if (!el.getAttribute('alt')) el.setAttribute('alt', 'Imagem embutida');
      } else if (/^http:\/\//i.test(src)) {
        el.setAttribute('src', src.replace(/^http:\/\//i, 'https://'));
      }
    }

    if (tag === 'a') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
  }

  return doc.body ? doc.body.innerHTML : '';
}
